import type { ElementType, RefObject } from 'react';
import { useState } from 'react';
import { styled } from 'styled-components';

import type { ApiFileUploadTypes } from 'api/types/ApiFileUploadTypes';
import { Box } from 'components/Box/Box';
import type { FieldCustomErrorMatcher } from 'components/Form/Field/FieldCustomErrorMatcher';
import { InputField } from 'components/Form/InputField';
import { LegacyBorderlessButton } from 'components/LegacyButton/LegacyBorderlessButton';
import { StyledNativeLinkWithRef } from 'components/Link/StyledNativeLinkWithRef';
import { colors } from 'theme/theme';
import { removeArrayIndex, updateArrayItem } from 'utils/functional/array';
import { uuid } from 'utils/functional/string/uuid';

export type MultiInputExtraData = {
  customErrorMatch?: FieldCustomErrorMatcher;
  includeCity?: boolean;
  includeFull?: boolean;
  label?: string;
  placeholder?: string;
  isRequired?: boolean;
  fileType?: ApiFileUploadTypes;
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onUpdateValue?: (...args: any[]) => any;
  listingType?: string;
};

const AddRemoveButton = styled(LegacyBorderlessButton)`
  font-size: 32px;
  transition: color 0.2s ease-in;

  &:hover {
    color: ${colors.brandGreen};
  }

  &:disabled {
    color: ${colors.elementGrey};
  }
`;

type Props = {
  id?: string;
  component?: ElementType | null | undefined;
  name: string;
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  values: (string | Record<string, any>)[];
  errorRef?: RefObject<HTMLDivElement | null>;
  maxInputs: number;
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onChange: (arg0: (arg0: any) => any[]) => void;
  type?: string;
  placeholder?: string;
  disabled?: boolean;
  extraData?: MultiInputExtraData[];
  hideControls?: boolean;
  addText?: string;
  removeText?: string;
  showEmpty?: boolean;
  startWithNone?: boolean;
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  defaultData?: any;
  'data-qa-id': string;
  sortBy?: (
    arg0: {
      value: unknown;
      index: number;
    },
    arg1: {
      value: unknown;
      index: number;
    },
  ) => number;
};

export function MultiInput({
  id,
  component,
  name,
  values,
  errorRef,
  maxInputs,
  onChange,
  type = 'text',
  placeholder,
  disabled,
  extraData = [],
  hideControls = false,
  addText,
  removeText,
  showEmpty,
  startWithNone,
  defaultData = '',
  sortBy,
  ...props
}: Props) {
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line no-param-reassign, no-nested-ternary
  values = values.length !== 0 ? values : startWithNone ? [] : [defaultData];
  const Component = component || InputField;
  const [ids, setIds] = useState(
    Array.from({ length: values.length })
      .fill(defaultData)
      .map(() => uuid()),
  );

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onInputChange = (index: any) => (ev: any) => {
    let update;
    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
    const valuesToAdd = ev && ev.target ? ev.target.value : ev;

    if (showEmpty && values.length > 1 && !ev) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      update = (oldValues: any) => removeArrayIndex(oldValues, index);

      setIds(removeArrayIndex(ids, index));
    } else {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      update = (oldValues: any) => {
        const newValues = updateArrayItem(oldValues, index, valuesToAdd);

        if (showEmpty && newValues.length >= values.length) {
          newValues.push(defaultData);
        }

        if (newValues.length > values.length) {
          setIds([...ids, uuid()]);
        }

        return newValues;
      };
    }

    onChange(update);
  };

  const addInputClicked = () => {
    const newValues = updateArrayItem(values, values.length, defaultData);
    onChange(() => newValues);
    setIds([...ids, uuid()]);
  };

  const removeInputClicked = () => {
    onChange(() => removeArrayIndex(values, values.length - 1));
    setIds(removeArrayIndex(ids, values.length - 1));
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const inputId = (index: any) => {
    if (!id) return undefined;
    return index === 0 ? id : `${id}-${index + 1}`;
  };

  const inputs = values
    .map((value, index) => ({
      value,
      index,
    }))
    .sort(sortBy);

  return (
    <>
      {inputs.map(({ index }) => (
        <Box mb="8px" key={ids[index] || index}>
          {removeText &&
            (values.length > 1 || (startWithNone && values.length > 0)) && (
              <Box mt={36} mb="8px">
                <StyledNativeLinkWithRef
                  href="#"
                  data-qa-id={`${props['data-qa-id']}-remove`}
                  onClick={(e) => {
                    e.preventDefault();

                    if (!disabled) {
                      removeInputClicked();
                    }
                  }}
                >
                  - {removeText}
                </StyledNativeLinkWithRef>
              </Box>
            )}
          <Component
            id={inputId(index)}
            name={`${name}.${index}`}
            data-qa-id={props['data-qa-id']}
            type={type}
            placeholder={placeholder}
            onChange={onInputChange(index)}
            disabled={disabled}
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...extraData[index]}
            noMargin
          />
          <div ref={errorRef} />
        </Box>
      ))}

      {addText && values.length < maxInputs && (
        <Box mt={36}>
          <StyledNativeLinkWithRef
            href="#"
            data-qa-id={`${props['data-qa-id']}-add`}
            onClick={(e) => {
              e.preventDefault();

              if (!disabled) {
                addInputClicked();
              }
            }}
          >
            + {addText}
          </StyledNativeLinkWithRef>
        </Box>
      )}
      {!hideControls && (
        <>
          <AddRemoveButton
            mr="10px"
            p="0"
            disabled={disabled || values.length === maxInputs}
            data-qa-id={`${props['data-qa-id']}-add`}
            onClick={addInputClicked}
          >
            + {/* TODO: use icon */}
          </AddRemoveButton>
          <AddRemoveButton
            p="0"
            disabled={disabled || values.length === 1}
            data-qa-id={`${props['data-qa-id']}-remove`}
            onClick={removeInputClicked}
          >
            - {/* TODO: use icon */}
          </AddRemoveButton>
        </>
      )}
    </>
  );
}
